import { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay, addMinutes } from "date-fns";
import enUS from "date-fns/locale/en-US";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../components/calendar.css";

import { useTasks } from "../contexts/TaskContext";
import TaskModal from "../components/TaskModal";
import DayBlocksView from "../components/DayBlocksView";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { getWorkspace } from "../lib/workspaces";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Globe,
  Copy,
  Check,
  RotateCcw,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const DnDCalendar = withDragAndDrop(Calendar);

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

const PRIORITY_TINT = {
  urgent: "#EF4444",
  important: "#F59E0B",
  normal: "#0281FF",
  low: "#64748B",
};

const VIEW_OPTIONS = [
  { v: Views.MONTH, l: "Month" },
  { v: Views.WEEK, l: "Week" },
  { v: Views.DAY, l: "Day" },
  { v: Views.AGENDA, l: "Agenda" },
  { v: "blocks", l: "Day Blocks" },
];

function taskToEvent(task) {
  const start = new Date(`${task.due_date}T${task.start_time || task.due_time || "09:00"}:00`);
  let end;
  if (task.start_time && task.end_time) {
    end = new Date(`${task.due_date}T${task.end_time}:00`);
  } else if (task.due_time) {
    end = addMinutes(start, 30);
  } else {
    // All-day event — span the full day
    end = new Date(start);
    end.setHours(23, 59);
  }
  const allDay = !task.due_time && !task.start_time && !task.end_time;
  return {
    id: task.id,
    title: task.title,
    start,
    end,
    allDay,
    resource: task,
  };
}

function pad(n) { return n.toString().padStart(2, "0"); }
function dateToISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function dateToTime(d) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CalendarPage() {
  const { tasks, updateTask, refresh } = useTasks();
  const [view, setView] = useState(() => {
    // Honour ?view=blocks or ?view=day from PWA shortcuts/URL
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      const v = sp.get("view");
      if (v === "blocks") return "blocks";
      if (v === "day") return Views.DAY;
      if (v === "week") return Views.WEEK;
      if (v === "agenda") return Views.AGENDA;
    }
    return Views.MONTH;
  });
  const [date, setDate] = useState(new Date());
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createDefaults, setCreateDefaults] = useState(null);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribe, setSubscribe] = useState(null);
  const [copied, setCopied] = useState(false);
  const [dayBlockRanges, setDayBlockRanges] = useState(null);

  // Refresh whenever the page is opened to ensure recurring tasks etc. are fresh
  useEffect(() => {
    refresh();
    // Pull the user's customised day-block ranges, if any
    api
      .get("/settings")
      .then(({ data }) => setDayBlockRanges(data?.day_block_ranges || null))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = useMemo(
    () => tasks.filter((t) => t.due_date).map(taskToEvent),
    [tasks]
  );

  const eventStyleGetter = (event) => {
    const t = event.resource;
    const wsColor = getWorkspace(t.dashboard).color;
    const prioColor = PRIORITY_TINT[t.priority] || PRIORITY_TINT.normal;
    return {
      style: {
        backgroundColor: wsColor,
        borderLeft: `3px solid ${prioColor}`,
        opacity: t.status === "done" ? 0.55 : 1,
        textDecoration: t.status === "done" ? "line-through" : "none",
      },
    };
  };

  const onEventDrop = useCallback(
    async ({ event, start, end, isAllDay }) => {
      const task = event.resource;
      const updates = {
        due_date: dateToISO(start),
      };
      if (isAllDay) {
        updates.due_time = null;
        updates.start_time = null;
        updates.end_time = null;
      } else if (task.start_time && task.end_time) {
        updates.start_time = dateToTime(start);
        updates.end_time = dateToTime(end);
        // Keep due_time aligned with start
        updates.due_time = updates.start_time;
      } else {
        updates.due_time = dateToTime(start);
      }
      try {
        await api.patch(`/calendar/events/${task.id}/reschedule`, updates);
        await refresh();
        toast.success(`Rescheduled "${task.title}"`);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Reschedule failed");
      }
    },
    [refresh]
  );

  const onEventResize = useCallback(
    async ({ event, start, end }) => {
      const task = event.resource;
      try {
        await api.patch(`/calendar/events/${task.id}/reschedule`, {
          due_date: dateToISO(start),
          start_time: dateToTime(start),
          end_time: dateToTime(end),
          due_time: dateToTime(start),
        });
        await refresh();
        toast.success(`Resized "${task.title}"`);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Resize failed");
      }
    },
    [refresh]
  );

  const onSelectSlot = useCallback(({ start, end }) => {
    // Single click on a day in month view → quick create dialog
    setCreateDefaults({
      due_date: dateToISO(start),
      start_time: view !== Views.MONTH ? dateToTime(start) : null,
      end_time: view !== Views.MONTH ? dateToTime(end) : null,
      due_time: view !== Views.MONTH ? dateToTime(start) : null,
    });
    setCreating(true);
  }, [view]);

  const navigate = (dir) => {
    const next = new Date(date);
    if (view === Views.MONTH) {
      next.setMonth(next.getMonth() + (dir === "next" ? 1 : -1));
    } else if (view === Views.WEEK) {
      next.setDate(next.getDate() + (dir === "next" ? 7 : -7));
    } else if (view === Views.DAY) {
      next.setDate(next.getDate() + (dir === "next" ? 1 : -1));
    } else {
      next.setDate(next.getDate() + (dir === "next" ? 7 : -7));
    }
    setDate(next);
  };

  const headerLabel = useMemo(() => {
    if (view === Views.MONTH) {
      return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    }
    if (view === Views.WEEK) {
      const ws = startOfWeek(date, { weekStartsOn: 0 });
      const we = new Date(ws);
      we.setDate(we.getDate() + 6);
      return `${ws.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${we.toLocaleDateString(
        undefined,
        { month: "short", day: "numeric", year: "numeric" }
      )}`;
    }
    if (view === Views.DAY || view === "blocks") {
      return date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
    return "Agenda";
  }, [view, date]);

  // ---------- Subscribe modal ----------
  const openSubscribe = async () => {
    setSubscribeOpen(true);
    if (!subscribe) {
      try {
        const { data } = await api.get("/calendar/subscribe");
        setSubscribe(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't get subscribe URL");
      }
    }
  };

  const onCopy = async () => {
    if (!subscribe?.url) return;
    try {
      await navigator.clipboard.writeText(subscribe.url);
      setCopied(true);
      toast.success("Calendar URL copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const onRotate = async () => {
    if (!window.confirm("Rotate URL? Existing subscribers will stop receiving updates.")) return;
    try {
      const { data } = await api.post("/calendar/subscribe/rotate");
      setSubscribe(data);
      setCopied(false);
      toast.success("New URL generated — share it with new subscribers");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rotate failed");
    }
  };

  return (
    <div
      data-testid="calendar-page"
      className="px-4 sm:px-8 py-8 max-w-[1600px] mx-auto"
    >
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 anim-fade-up">
        <div className="flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              backgroundColor: "hsla(var(--primary), 0.12)",
              border: "1px solid hsla(var(--primary), 0.35)",
              color: "hsl(var(--primary))",
            }}
          >
            <CalendarDays size={26} strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] mb-2 font-bold text-primary">
              Calendar
            </div>
            <h1
              data-testid="calendar-page-title"
              className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-none"
            >
              {headerLabel}
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Drag tasks across days to reschedule. Click a slot to add a new task.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={openSubscribe}
            data-testid="calendar-subscribe-btn"
          >
            <Globe size={14} className="mr-1.5" /> Subscribe
          </Button>
          <Button
            onClick={() => {
              setCreateDefaults({ due_date: dateToISO(new Date()) });
              setCreating(true);
            }}
            data-testid="calendar-new-task-btn"
          >
            <Plus size={14} className="mr-1.5" /> New task
          </Button>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        {/* View switcher */}
        <div
          data-testid="calendar-view-switcher"
          className="inline-flex rounded-md border border-border p-1 bg-card"
        >
          {VIEW_OPTIONS.map((opt) => {
            const active = view === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => setView(opt.v)}
                data-testid={`calendar-view-${opt.v}`}
                className={cn(
                  "px-3 py-1 text-xs font-medium uppercase tracking-wider rounded transition-colors",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {opt.l}
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("prev")}
            data-testid="calendar-prev-btn"
          >
            <ChevronLeft size={15} />
          </Button>
          <Button
            variant="outline"
            onClick={() => setDate(new Date())}
            data-testid="calendar-today-btn"
            className="text-xs"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("next")}
            data-testid="calendar-next-btn"
          >
            <ChevronRight size={15} />
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <div data-testid="calendar-grid" className="anim-fade-up delay-1">
        {view === "blocks" ? (
          <DayBlocksView
            tasks={tasks}
            selectedDate={dateToISO(date)}
            onSelectDate={(iso) => setDate(new Date(`${iso}T00:00:00`))}
            onEditTask={(t) => setEditing(t)}
            onCreateTask={(defaults) => {
              setCreateDefaults(defaults);
              setCreating(true);
            }}
            refreshTasks={refresh}
            ranges={dayBlockRanges}
          />
        ) : (
          <DnDCalendar
            localizer={localizer}
            events={events}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            startAccessor="start"
            endAccessor="end"
            selectable
            resizable
            draggableAccessor={() => true}
            onEventDrop={onEventDrop}
            onEventResize={onEventResize}
            onSelectEvent={(ev) => setEditing(ev.resource)}
            onSelectSlot={onSelectSlot}
            eventPropGetter={eventStyleGetter}
            style={{ height: "calc(100dvh - 320px)", minHeight: 500 }}
            popup
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-4 flex-wrap text-[10px] uppercase tracking-wider text-muted-foreground">
        <span className="font-bold">Workspace:</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: getWorkspace("personal").color }} /> Personal
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: getWorkspace("family").color }} /> Family
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: getWorkspace("work").color }} /> Work
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: getWorkspace("school").color }} /> School
        </span>
        <span className="font-bold ml-3">Priority:</span>
        {Object.entries(PRIORITY_TINT).map(([p, c]) => (
          <span key={p} className="inline-flex items-center gap-1.5 capitalize">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} /> {p}
          </span>
        ))}
      </div>

      {/* Edit existing task */}
      <TaskModal
        open={!!editing}
        onClose={() => setEditing(null)}
        task={editing}
      />

      {/* Create new task (with calendar-provided defaults) */}
      <TaskModal
        open={creating}
        onClose={() => {
          setCreating(false);
          setCreateDefaults(null);
        }}
        defaultValues={createDefaults}
      />

      {/* Subscribe dialog */}
      <Dialog open={subscribeOpen} onOpenChange={setSubscribeOpen}>
        <DialogContent
          data-testid="calendar-subscribe-dialog"
          className="max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight flex items-center gap-2">
              <Globe size={18} /> Subscribe to your calendar
            </DialogTitle>
            <DialogDescription>
              Add this URL to any calendar app (Google, Apple, Outlook, Fantastical, …)
              for a read-only feed of your FlowTasks tasks. Updates flow automatically.
            </DialogDescription>
          </DialogHeader>

          {subscribe ? (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Calendar URL (https)
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={subscribe.url}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    data-testid="calendar-subscribe-url"
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    onClick={onCopy}
                    data-testid="calendar-copy-url-btn"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
              </div>
              <div className="rounded-md bg-secondary/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-bold">
                  Quick add (auto-subscribe)
                </div>
                <a
                  href={subscribe.webcal_url}
                  className="text-xs font-mono text-primary hover:underline break-all"
                  data-testid="calendar-webcal-link"
                >
                  {subscribe.webcal_url}
                </a>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Tap on iOS/macOS to open the Calendar app — it'll auto-add the
                  subscription.
                </p>
              </div>

              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer font-medium">Setup help</summary>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    <strong>Google Calendar:</strong> Settings → Add calendar → From URL
                  </li>
                  <li>
                    <strong>Apple Calendar:</strong> File → New Calendar Subscription
                  </li>
                  <li>
                    <strong>Outlook:</strong> Add calendar → Subscribe from web
                  </li>
                </ul>
              </details>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-4 text-center">
              Generating your URL…
            </div>
          )}

          <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={onRotate}
              data-testid="calendar-rotate-url-btn"
              className="text-amber-500 hover:text-amber-500 hover:bg-amber-500/10"
              disabled={!subscribe}
            >
              <RotateCcw size={14} className="mr-1.5" /> Rotate URL
            </Button>
            <Button
              variant="default"
              onClick={() => setSubscribeOpen(false)}
              data-testid="calendar-subscribe-close-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
